import React, { useState, useEffect, useCallback } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  fetchTestVeriTemizleDurum,
  runTestVeriTemizle,
} from '../services/testVeriTemizleService';
import './TestVeriTemizlePage.css';

const getErrorMessage = (err, fallback) =>
  err?.message || err?.data?.mesaj || fallback;

const TestVeriTemizlePage = () => {
  const { confirm } = useConfirmDialog();
  const [durum, setDurum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [log, setLog] = useState('');
  const [cihazlariSil, setCihazlariSil] = useState(false);

  const loadDurum = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchTestVeriTemizleDurum();
      setDurum(res?.veri || null);
    } catch (e) {
      setError(getErrorMessage(e, 'Durum yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDurum();
  }, [loadDurum]);

  const handleRun = async () => {
    const persona = durum?.persona;
    const musteriMetinleri = [
      persona?.musteri?.adSoyad,
      ...(persona?.ekMusteriler || []).map((m) => m.adSoyad),
    ].filter(Boolean);
    const tsMetinleri = [
      persona?.teknikServis?.ad,
      ...(persona?.ekTeknikServisler || []).map((t) => t.ad),
    ].filter(Boolean);
    const tekMetinleri = [
      persona?.teknisyen?.adSoyad,
      ...(persona?.ekTeknisyenler || []).map((t) => t.adSoyad),
    ].filter(Boolean);
    const cihazMetni = cihazlariSil
      ? ' Test müşterilerin cihazları da silinecek.'
      : '';

    const ok = await confirm({
      title: 'Test verilerini sil',
      message: `${tsMetinleri.join(', ') || 'Test teknik servis'}, ${tekMetinleri.join(', ') || 'test teknisyen'} ve ${musteriMetinleri.join(', ') || 'test müşteriler'} ile ilişkili işlem verileri kalıcı olarak silinecek.${cihazMetni} Devam edilsin mi?`,
      confirmText: 'Evet, sil',
      cancelText: 'Vazgeç',
    });
    if (!ok) return;

    setRunning(true);
    setError('');
    setLog('');
    try {
      const res = await runTestVeriTemizle({ cihazlariSil });
      const veri = res?.veri || {};
      setLog(veri.logMetin || (veri.loglar || []).join('\n') || res?.mesaj || '');
      if (!res?.basarili) {
        setError(res?.mesaj || 'Temizlik tamamlanamadı');
      }
    } catch (e) {
      setError(getErrorMessage(e, 'Temizlik çalıştırılamadı'));
      const veri = e?.data?.veri;
      if (veri?.logMetin) setLog(veri.logMetin);
      else if (veri?.loglar?.length) setLog(veri.loglar.join('\n'));
    } finally {
      setRunning(false);
    }
  };

  const persona = durum?.persona;
  const izinli = durum?.izinli !== false;

  return (
    <div className="page-container test-veri-temizle-page">
      <div className="page-header">
        <h1>Test verilerini sil</h1>
      </div>

      {error ? <div className="test-veri-alert test-veri-alert-error">{error}</div> : null}

      <section className="test-veri-card">
        <h2>Test hesapları</h2>
        {loading ? (
          <p className="test-veri-muted">Yükleniyor…</p>
        ) : (
          <ul className="test-veri-persona-list">
            <li>
              <strong>Test Teknik Servisi</strong>
              <span>{persona?.teknikServis?.ad || '—'}</span>
              <code>{persona?.teknikServis?.kullaniciAdi || 'ateknik'}</code>
            </li>
            {(persona?.ekTeknikServisler || []).map((t) => (
              <li key={t.id || t.kullaniciAdi}>
                <strong>Test Teknik Servisi</strong>
                <span>{t.ad || '—'}</span>
                <code>{t.kullaniciAdi || '—'}</code>
              </li>
            ))}
            <li>
              <strong>Test Teknisyen</strong>
              <span>{persona?.teknisyen?.adSoyad || '—'}</span>
              <code>{persona?.teknisyen?.kullaniciAdi || 'ateknisyen'}</code>
            </li>
            {(persona?.ekTeknisyenler || []).map((t) => (
              <li key={t.id || t.kullaniciAdi}>
                <strong>Test Teknisyen</strong>
                <span>{t.adSoyad || '—'}</span>
                <code>{t.kullaniciAdi || '—'}</code>
              </li>
            ))}
            <li>
              <strong>Test Müşteri</strong>
              <span>{persona?.musteri?.adSoyad || '—'}</span>
              <code>{persona?.musteri?.telefon || '—'}</code>
            </li>
            {(persona?.ekMusteriler || []).map((m) => (
              <li key={m.id || m.telefon || m.adSoyad}>
                <strong>Ek Test Müşteri</strong>
                <span>{m.adSoyad || '—'}</span>
                <code>{m.telefon || '—'}</code>
              </li>
            ))}
          </ul>
        )}

        <p className="test-veri-muted">
          Bu hesaplarla ilişkili randevu, servis kaydı, sipariş, iade, kargo, cihaz değişimi,
          cari hareketler ve stok kayıtları silinir. Kullanıcı ve müşteri kayıtları korunur.
        </p>

        <label className="test-veri-option">
          <input
            type="checkbox"
            checked={cihazlariSil}
            onChange={(e) => setCihazlariSil(e.target.checked)}
            disabled={running || loading}
          />
          <span>Test müşterilerin cihazlarını da sil</span>
        </label>

        <div className="test-veri-actions">
          <button
            type="button"
            className="primary-btn test-veri-run-btn"
            onClick={handleRun}
            disabled={loading || running || !izinli || !persona}>
            {running ? 'Çalışıyor…' : 'Test verilerini sil'}
          </button>
        </div>
      </section>

      <section className="test-veri-card test-veri-log-card">
        <div className="test-veri-log-head">
          <h2>Çıktı</h2>
          {log ? (
            <button type="button" className="secondary-btn" onClick={() => setLog('')}>
              Temizle
            </button>
          ) : null}
        </div>
        <pre className="test-veri-log">
          {log || 'Temizlik çalıştırıldığında satır satır log burada görünür.'}
        </pre>
      </section>
    </div>
  );
};

export default TestVeriTemizlePage;
